import { Vector3 } from "three";
import { customElement, property } from "lit/decorators.js";
import { tryRaycastOnPlaneFromScreen } from "../../physics/PhysicsUtil";
import { createDisposable } from "../events/messaging/index";
import { ViewportCapabilityElement } from "../viewport/ViewportCapabilityElement";
import type { ViewportContext } from "../viewport/ViewportContext";
import {
  endItemDrag,
  readItemDragPayload,
  type SugarItemDragPayload,
} from "./itemDragSource";

export const SUGAR_ITEM_DRAG_ENTER_EVENT = "sugar-item-drag-enter";
export const SUGAR_ITEM_DRAG_OVER_EVENT = "sugar-item-drag-over";
export const SUGAR_ITEM_DRAG_LEAVE_EVENT = "sugar-item-drag-leave";
export const SUGAR_ITEM_DROP_EVENT = "sugar-item-drop";

export interface SugarItemDragDetail {
  /**
   * Sürüklenen öğe. `dragover` sırasında `undefined` olabilir — tarayıcı
   * `dataTransfer`'ı okutmaz ve aynı doküman yedeği de yoksa (ör. sürükleme
   * başka bir sekmeden geliyorsa) yük ancak `drop` anında bilinir.
   */
  readonly payload?: SugarItemDragPayload;
  /** Zemin düzlemindeki dünya koordinatı; hayalet modeli buraya koy. */
  readonly point?: Vector3;
  readonly originalEvent: DragEvent;
}

const GROUND_PLANE = {
  position: new Vector3(0, 0, 0),
  normal: new Vector3(0, 1, 0),
};

/**
 * `<sugar-item-drop>` — UI panelinden viewport'a öğe sürüklemenin **hedef**
 * tarafı. Kaynak tarafı `itemDragSource.startItemDrag`.
 *
 * `<sugar-file-drop>` işletim sisteminden gelen dosyalar içindir; bu element
 * onun tamamlayıcısı ve ikisi birbirini dışlar (biri `Files` taşıyan
 * sürüklemede çalışır, diğeri çalışmaz), yani ikisi aynı viewport'ta yan yana
 * durabilir.
 *
 * Akış — üç olay, üç iş:
 *
 * ```
 * sugar-item-drag-enter  → hayalet modeli yarat (yükleme başlat)
 * sugar-item-drag-over   → hayaleti detail.point'e taşı
 * sugar-item-drop        → kalıcı yap (commit)
 * sugar-item-drag-leave  → hayaleti kaldır (iptal)
 * ```
 *
 * ```html
 * <sugar-viewport>
 *   <sugar-item-drop accept="product,module"></sugar-item-drop>
 * </sugar-viewport>
 * ```
 *
 * Element yükleme yapmaz, sahneye bir şey koymaz. Ne yaratılacağına ürün karar
 * verir — `sugar-model-viewer`'da ürün değiştirmek, `sugar-configurator`'da
 * modül eklemek, `sugar-room-designer`'da odaya mobilya koymak demek.
 */
@customElement("sugar-item-drop")
export class SugarItemDropElement extends ViewportCapabilityElement {
  /**
   * Kabul edilen `payload.kind` listesi (virgülle ayrılmış).
   * Boş bırakılırsa hepsi kabul edilir.
   */
  @property({ type: String })
  accept = "";

  /** Sürükleme sırasında viewport host'una eklenen class. */
  @property({ type: String, attribute: "active-class" })
  activeClass = "sugar-item-drop-active";

  private depth = 0;
  private entered = false;

  protected override onViewportConnected(context: ViewportContext) {
    const host = context.host;
    const onDragEnter = (e: DragEvent) => this.handleDragEnter(e, context, host);
    const onDragOver = (e: DragEvent) => this.handleDragOver(e, context);
    const onDragLeave = (e: DragEvent) => this.handleDragLeave(e, host);
    const onDrop = (e: DragEvent) => this.handleDrop(e, context, host);

    host.addEventListener("dragenter", onDragEnter);
    host.addEventListener("dragover", onDragOver);
    host.addEventListener("dragleave", onDragLeave);
    host.addEventListener("drop", onDrop);

    return createDisposable(() => {
      host.removeEventListener("dragenter", onDragEnter);
      host.removeEventListener("dragover", onDragOver);
      host.removeEventListener("dragleave", onDragLeave);
      host.removeEventListener("drop", onDrop);
      host.classList.remove(this.activeClass);
      this.depth = 0;
      this.entered = false;
    });
  }

  /** Dosya sürüklemesi `<sugar-file-drop>`'un işi; burada karışma. */
  private carriesFiles(event: DragEvent): boolean {
    const types = event.dataTransfer?.types;
    return !!types && Array.from(types).includes("Files");
  }

  /**
   * Bu sürükleme bu yüzeye ait mi.
   *
   * Yük okunamıyorsa (`dragover`'ın normal hâli) **kabul edilir**. Aksi hâlde
   * `preventDefault()` çağrılmaz, tarayıcı `drop`'u hiç ateşlemez ve sürükleme
   * sessizce ölür. Kesin filtreleme `drop` anında yapılır; orada `dataTransfer`
   * okunabilir.
   */
  private isOurs(event: DragEvent): boolean {
    if (this.carriesFiles(event)) return false;
    const payload = readItemDragPayload(event);
    if (!payload) return true;
    return this.matchesAccept(payload);
  }

  private matchesAccept(payload: SugarItemDragPayload): boolean {
    const filter = this.accept.trim();
    if (!filter) return true;
    const kinds = filter.split(",").map((k) => k.trim()).filter(Boolean);
    return kinds.includes(payload.kind);
  }

  /** Bilinen yükü döner; bilinmiyorsa `undefined`. */
  private payloadOf(event: DragEvent): SugarItemDragPayload | undefined {
    const payload = readItemDragPayload(event);
    return payload && this.matchesAccept(payload) ? payload : undefined;
  }

  private worldPoint(
    event: DragEvent,
    context: ViewportContext,
  ): Vector3 | undefined {
    const camera = context.engine.activeCamera?.instance;
    const canvas = context.engine.canvas;
    if (!camera || !canvas) return undefined;
    return tryRaycastOnPlaneFromScreen(event, GROUND_PLANE, camera, canvas);
  }

  private handleDragEnter(
    event: DragEvent,
    context: ViewportContext,
    host: HTMLElement,
  ): void {
    if (!this.isOurs(event)) return;
    event.preventDefault();
    this.depth += 1;
    if (this.depth > 1 || this.entered) return;

    this.entered = true;
    host.classList.add(this.activeClass);
    this.emit(SUGAR_ITEM_DRAG_ENTER_EVENT, {
      payload: this.payloadOf(event),
      point: this.worldPoint(event, context),
      originalEvent: event,
    });
  }

  private handleDragOver(event: DragEvent, context: ViewportContext): void {
    if (!this.isOurs(event)) return;
    // Bu olmadan `drop` ateşlenmez.
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = "copy";

    this.emit(SUGAR_ITEM_DRAG_OVER_EVENT, {
      payload: this.payloadOf(event),
      point: this.worldPoint(event, context),
      originalEvent: event,
    });
  }

  private handleDragLeave(event: DragEvent, host: HTMLElement): void {
    if (this.carriesFiles(event) || !this.entered) return;
    this.depth = Math.max(0, this.depth - 1);
    if (this.depth > 0) return;

    this.entered = false;
    host.classList.remove(this.activeClass);
    this.dispatchEvent(
      new CustomEvent(SUGAR_ITEM_DRAG_LEAVE_EVENT, {
        bubbles: true,
        composed: true,
      }),
    );
  }

  private handleDrop(
    event: DragEvent,
    context: ViewportContext,
    host: HTMLElement,
  ): void {
    const wasOurs = this.isOurs(event);
    this.depth = 0;
    this.entered = false;
    host.classList.remove(this.activeClass);
    if (!wasOurs) return;

    // `drop` anında dataTransfer okunabilir — kesin filtre burada.
    const payload = this.payloadOf(event);
    event.preventDefault();
    this.emit(SUGAR_ITEM_DROP_EVENT, {
      payload,
      point: this.worldPoint(event, context),
      originalEvent: event,
    });
    // Kaynak taraf `dragend` alamayabilir (ör. shadow DOM sınırı) — yedeği
    // burada da temizle ki bir sonraki sürükleme eski yükü görmesin.
    endItemDrag();
  }

  private emit(type: string, detail: SugarItemDragDetail): void {
    this.dispatchEvent(
      new CustomEvent<SugarItemDragDetail>(type, {
        detail,
        bubbles: true,
        composed: true,
      }),
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "sugar-item-drop": SugarItemDropElement;
  }
}
